// ─── API BASE ─────────────────────────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production.up.railway.app");

const TOKEN_KEY = "emr_token";

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || null;
}

async function api(method, path, body = null, isFormData = false) {
  const token = getToken();
  const headers = isFormData ? {} : { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const opts = {
    method,
    headers,
    body: body ? (isFormData ? body : JSON.stringify(body)) : null,
    redirect: "follow",
  };
  const res = await fetch(`${BASE}${path}`, opts);

  if (res.status === 401) {
    // Token expired or invalid — clear and redirect to login
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("emr_user");
    const err = await res.json().catch(() => ({ detail: "Session expired" }));
    throw new Error(err.detail || "Session expired. Please log in again.");
  }

  if (res.status === 429) {
    const err = await res.json().catch(() => ({ detail: "Too many attempts" }));
    throw new Error(err.detail || "Too many login attempts. Please wait.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = Array.isArray(err.detail)
      ? err.detail.map(e => e.msg || JSON.stringify(e)).join("; ")
      : err.detail || "Request failed";
    throw new Error(detail);
  }
  return res.json();
}

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:            (username, password)                        => api("POST", "/auth/login",            { username, password }),
  verifyToken:      (token)                                     => api("POST", "/auth/verify",            { token }),
  changePassword:   (role, new_password, editor_role)          => api("POST", "/auth/change-password",   { role, new_password, editor_role }),
  emergencyReset:   (master_code, new_password, role = "admin")=> api("POST", "/auth/emergency-reset",   { master_code, new_password, role }),
  updateMasterCode: (current_master_code, new_master_code)     => api("POST", "/auth/update-master-code",{ current_master_code, new_master_code }),
  getCredentials:   ()                                         => api("GET",  "/auth/credentials"),
  getAuditLog:      (limit = 100)                              => api("GET",  `/auth/audit-log?limit=${limit}`),
};

// ─── PATIENTS ─────────────────────────────────────────────────────────────────
export const patientAPI = {
  list:   (search = "", page = 1, pageSize = 20) => api("GET", `/patients?search=${encodeURIComponent(search)}&page=${page}&page_size=${pageSize}`),
  listAll:(search = "")  => api("GET",    `/patients/all${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  get:    (id, reportPage=1, reportPageSize=20) => api("GET", `/patients/${id}?report_page=${reportPage}&report_page_size=${reportPageSize}`),
  create: (data)         => api("POST",   "/patients", data),
  update: (id, data)     => api("PUT",    `/patients/${id}`, data),
  delete: (id)           => api("DELETE", `/patients/${id}`),
};

// ─── REPORTS ──────────────────────────────────────────────────────────────────
export const reportAPI = {
  list:   (params = {}) => {
    const q = new URLSearchParams();
    if (params.search)      q.set("search",       params.search);
    if (params.page)        q.set("page",          params.page);
    if (params.pageSize)    q.set("page_size",     params.pageSize);
    if (params.reportType)  q.set("report_type",   params.reportType);
    if (params.dateFilter)  q.set("date_filter",   params.dateFilter);
    if (params.statusFilter) q.set("status_filter", params.statusFilter);
    return api("GET", `/reports?${q.toString()}`);
  },
  upload: (formData)               => api("POST",   "/reports/upload",       formData, true),
  get:    (id)                     => api("GET",    `/reports/${id}`),
  edit:   (id, values, editorRole) => api("PUT",    `/reports/${id}/values`, { values, editor_role: editorRole }),
  delete: (id)                     => api("DELETE", `/reports/${id}`),
  pdfUrl: (id)                     => `${BASE}/reports/${id}/pdf`,
};

// ─── IMAGING ─────────────────────────────────────────────────────────────────
export const imagingAPI = {
  upload:        (formData)   => api("POST",   "/imaging/upload",          formData, true),
  getForPatient: (patientId)  => api("GET",    `/imaging/patient/${patientId}`),
  get:           (id)         => api("GET",    `/imaging/${id}`),
  update:        (id, data)   => api("PUT",    `/imaging/${id}`, data),
  delete:        (id)         => api("DELETE", `/imaging/${id}`),
  fileUrl:       (id)         => `${BASE}/imaging/${id}/file`,
};

// ─── EXCEL ────────────────────────────────────────────────────────────────────
export const excelAPI = {
  exportUrl: (patientId) => `${BASE}/excel/export/${patientId}`,
  import:    (formData)  => api("POST", "/excel/import",       formData, true),
  bulkImport:(formData)  => api("POST", "/excel/bulk-import",  formData, true),
  importBatch:(formData) => api("POST", "/excel/import-batch", formData, true),
  templateUrl: ()        => `${BASE}/excel/template`,
};

// ─── SYSTEM ───────────────────────────────────────────────────────────────────
export const systemAPI = {
  health:  () => api("GET", "/health"),
  status:  () => api("GET", "/status"),
  errors:  (limit = 50) => api("GET", `/errors?limit=${limit}`),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
export function normalizeReport(r) {
  return {
    id:        r.id,
    type:      r.type,
    date:      r.date,
    time:      r.time || "",
    lab:       r.lab || "",
    labId:     r.lab_id || "",
    pdfName:   r.pdf_name || "",
    storageUrl: r.storage_url || "",
    createdAt:  r.created_at || r.date,
    editedBy:  r.edited_by || null,
    editedAt:  r.edited_at || null,
    values: (r.values || []).map(v => ({
      name:      v.name,
      value:     v.value ?? (parseFloat(v.value_text) || null),
      valueText: v.value_text || String(v.value ?? ""),
      unit:      v.unit || "",
      refMin:    v.ref_min ?? 0,
      refMax:    v.ref_max ?? 0,
      status:    v.status || (v.abnormal ? "Abnormal" : "Normal"),
      abnormal:  Boolean(v.abnormal),
    })),
  };
}

export function normalizePatient(p) {
  return {
    id:                p.id,
    name:              p.name,
    age:               p.age,
    sex:               p.sex,
    blood:             p.blood,
    phone:             p.phone,
    address:           p.address,
    doctor:            p.doctor,
    emergency_contact: p.emergency_contact,
    notes:             p.notes,
    registered:        p.registered,
    reports:           (p.reports || []).map(normalizeReport),
    report_count:      p.report_count || (p.reports || []).length,
    abnormal_count:    p.abnormal_count || 0,
    last_report_date:  p.last_report_date || null,
    total_reports:     p.total_reports || p.report_count || 0,
    report_page:       p.report_page || 1,
    report_page_size:  p.report_page_size || 20,
    report_total_pages: p.report_total_pages || 1,
    report_types:       p.report_types || [],
  };
}